// Authentication sagas
import { put } from 'redux-saga/effects';
import type { Action } from 'redux-actions';

// Services
import { ErrorCategory, handleError } from '@services/errorService';
import { setActiveSessionToken } from '@services/sessionToken';

// Api
import client from '@api';

// Actions
import { SelectOrganization } from '@actions/organization';
import {
  UserPasswordReset,
  UserSignIn,
  UserSignInWithGoogle,
  UserSignUp,
} from '@actions/user';
import { UpdateAccount } from '@actions/account';
import { SetRedirectControl } from '@actions/redirectControl';
import { SetFormError } from '@actions/formError';
import { SetLoading } from '@actions/loading';

import { applicationErrors } from '../../contstants/error';
import { signInGoogleMutation } from '../../graphql/mutations/account';

import { REDIRECT_PATH } from './constants';
import {
  mutateResendVerification,
  mutateResetPassword,
  mutateSignIn,
  mutateSignUp,
} from './apiHelpers';
import type {
  GoogleCredentialPayload,
  OAuthData,
  PasswordResetPayload,
  ResetPasswordResponse,
  SignInGoogleResponse,
  SignInPayload,
  SignInResponse,
  SignUpPayload,
} from './types';

export function* oAuthSaga({
  account,
  tokens,
}: OAuthData): Generator<unknown, void, unknown> {
  yield put(UpdateAccount(account));

  const firstOrg = account?.organizations?.[0];
  if (firstOrg) {
    yield put(SelectOrganization(firstOrg));
  }

  // Active session JWT: org JWT if the user has at least one org,
  // otherwise the account JWT (mid-onboarding case).
  if (firstOrg?.token) {
    setActiveSessionToken(firstOrg.token);
  } else if (tokens?.access) {
    setActiveSessionToken(tokens.access);
  }

  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    userID: account?._id,
    email: account?.email,
  });

  if (account?.profile && account?.profile?.onboardingPassed) {
    yield put(SetRedirectControl(REDIRECT_PATH.home));
  } else {
    yield put(SetRedirectControl('/onboarding/persona'));
  }
}

export function* signIn({
  payload,
}: Action<SignInPayload>): Generator<unknown, void, unknown> {
  try {
    yield put(SetLoading(true));

    const { returnTo, ...credentials } = payload;
    const response = (yield mutateSignIn(credentials)) as SignInResponse;
    const signInData = response.data.signIn;

    yield put(UpdateAccount(signInData.account));

    const firstOrg = signInData.account?.organizations?.[0];
    if (firstOrg) {
      yield put(SelectOrganization(firstOrg));
    }

    // Active session JWT: org JWT if the user has at least one org,
    // otherwise the account JWT (mid-onboarding case).
    if (firstOrg?.token) {
      setActiveSessionToken(firstOrg.token);
    } else if (signInData?.tokens?.access) {
      setActiveSessionToken(signInData.tokens.access);
    }

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      userID: signInData?.account?._id,
      email: signInData?.account?.email,
    });

    const fallbackPath = REDIRECT_PATH.home;
    const safeReturnTo =
      typeof returnTo === 'string' &&
      returnTo.startsWith('/') &&
      !returnTo.startsWith('//') &&
      !returnTo.startsWith('/auth/')
        ? returnTo
        : null;
    yield put(SetRedirectControl(safeReturnTo || fallbackPath));
  } catch (error) {
    const err = error as Error;
    // An account that never entered its sign-up code used to dead-end here:
    // signing in failed, signing up again said the email was taken. Send a
    // fresh code and reopen the code screen instead.
    if (isUnverified(err.message) && payload.email) {
      try {
        yield mutateResendVerification(payload.email);
      } catch (resendError) {
        handleError(resendError, {
          category: ErrorCategory.AUTH,
          silent: true,
        });
      }
      yield put(SetLoading(false));
      yield put(
        SetRedirectControl(
          REDIRECT_PATH.verifyEmail(encodeURIComponent(payload.email)),
        ),
      );
      return;
    }
    const parsed = applicationErrors.parse(err.message);
    yield put(
      SetFormError({
        form: 'signIn',
        error: parsed || { password: err.message || 'Invalid credentials' },
      }),
    );
    yield put(SetLoading(false));
  }
}

const isUnverified = (message?: string) =>
  /not verified|UNVERIFIED_ACCOUNT/i.test(message || '');

export function* signUp({
  payload,
}: Action<SignUpPayload>): Generator<unknown, void, unknown> {
  try {
    let organizationSecret: string | null = null;

    if (typeof localStorage === 'object') {
      organizationSecret = localStorage.getItem('organizationSecret');
    }

    yield put(SetLoading(true));
    yield mutateSignUp({
      ...payload,
      ...(organizationSecret
        ? { organizationInvitationToken: organizationSecret }
        : {}),
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      email: payload?.email,
      event: 'successfulSignup',
      phoneNumber: payload?.phoneNumber,
    });

    if (payload.email)
      yield put(
        SetRedirectControl(
          REDIRECT_PATH.verifyEmail(encodeURIComponent(payload.email)),
        ),
      );

    if (payload.phoneNumber)
      yield put(
        SetRedirectControl(
          REDIRECT_PATH.verifyPhone(encodeURIComponent(payload.phoneNumber)),
        ),
      );
  } catch (error) {
    const err = error as Error;
    const parsed = applicationErrors.parse(err.message);
    const taken = /already in use/i.test(err.message || '');
    yield put(
      SetFormError({
        form: 'signUp',
        error: taken
          ? {
              email:
                'An account with this email already exists. Sign in to continue; if it was never confirmed we will send a new code.',
            }
          : parsed || err.message,
      }),
    );
  } finally {
    yield put(SetLoading(false));
  }
}

export function* signInWithGoogle({
  payload,
}: Action<GoogleCredentialPayload>): Generator<unknown, void, unknown> {
  try {
    const { credential: tokenId } = payload;

    const response = (yield client().mutate({
      mutation: signInGoogleMutation,
      variables: {
        input: { googleToken: tokenId },
      },
    })) as SignInGoogleResponse;

    const signInGoogle = response.data.signInGoogle;
    const { _id, email } = signInGoogle?.account;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      userID: _id,
      event: 'googleLogin',
      email,
    });

    yield* oAuthSaga(signInGoogle);
  } catch (error) {
    handleError(error, { category: ErrorCategory.AUTH });
  }
}

const RESET_LINK_INVALID =
  'This reset link is invalid or has expired. Request a new one.';

/**
 * Forgot-password completion: `resetPassword(token, { newPassword })` returns a
 * fresh session, so the user lands signed in exactly like after `signIn`.
 */
export function* resetPassword({
  payload: { token, password },
}: Action<PasswordResetPayload>): Generator<unknown, void, unknown> {
  try {
    yield put(SetLoading(true));
    const response = (yield mutateResetPassword(
      token,
      password,
    )) as ResetPasswordResponse;
    yield* oAuthSaga(response.data.resetPassword);
  } catch (error) {
    const err = error as Error;
    handleError(err, { category: ErrorCategory.AUTH, silent: true });
    yield put(
      SetFormError({
        form: 'resetPassword',
        error: {
          password: /invalid or expired/i.test(err.message || '')
            ? RESET_LINK_INVALID
            : err.message || RESET_LINK_INVALID,
        },
      }),
    );
  } finally {
    yield put(SetLoading(false));
  }
}

// Action creators for watchers
export const authActions = {
  UserSignIn,
  UserSignUp,
  UserSignInWithGoogle,
  UserPasswordReset,
};
